import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faClose } from "@fortawesome/pro-solid-svg-icons/faClose";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import { EntityType } from "@mapmycustomers/shared";
import DefaultActivityTypeName from "@mapmycustomers/shared/enum/activity/DefaultActivityTypeName";
import { CustomFieldError } from "@mapmycustomers/shared/types/customField/CustomFieldValuesUpsertResponse";
import { DataAttributes } from "@mapmycustomers/shared/types/dataAttributes";
import EmailAssociatedEntity from "@mapmycustomers/shared/types/email/EmailAssociatedEntity";
import EmailSchedule from "@mapmycustomers/shared/types/email/EmailSchedule";
import { EntitiesSupportedByEmailFeature } from "@mapmycustomers/shared/types/entity";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import { useNonNullableContext } from "@mapmycustomers/shared/util/react";
import {
  Button,
  Divider,
  EmailEditorsProvider,
  EmailRecipientList,
  Modal,
  ModalStatus,
  Popconfirm,
} from "@mapmycustomers/ui";

import useDraftSavedLabel from "@app/component/email/creation/EmailComposeModal/useDraftSavedLabel";
import EmailDraftsButton from "@app/component/email/creation/EmailDraftsButton";
import Path from "@app/enum/Path";
import UserSetting from "@app/enum/UserSetting";
import { getEmailActivityType } from "@app/store/activity";
import { ActivityCreationPayloadWithCustomFields } from "@app/store/api/ApiService";
import { doesNeedToOpenActivityFormAfterSendingEmail, getEmailQuota } from "@app/store/email";
import {
  fetchEmailDrafts,
  fetchEmailQuota,
  fetchNewEmailAssociatedEntities,
} from "@app/store/email/actions";
import { sendEmail } from "@app/store/email/actions";
import { getCurrentUser, getUserSettingValue } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import EmailEntityOpenCallback from "@app/types/email/EmailEntityOpenCallback";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import useAnalytics from "@app/util/contexts/useAnalytics";
import resolvePrimaryEmailRecipientId from "@app/util/email/resolvePrimaryEmailRecipientId";
import useEmailDraft from "@app/util/email/useEmailDraft";
import { activityLayoutModel } from "@app/util/layout/impl";

import EmailRecipientTypeSelect from "../../EmailRecipientTypeSelect";
import AIInlineWriter from "../AIEmailWriter/AIInlineWriter";
import AISubjectField from "../AIEmailWriter/AISubjectField";
import EmailActivitiesCreateModal, {
  EmailActivitiesCreateModalProps,
} from "../EmailActivitiesCreateModal";
import EmailBody from "../EmailBody";
import EmailContext from "../EmailContext";
import EmailPreviewModal from "../EmailPreviewModal";
import AddEmailRecipientsModal from "../EmailRecipientsAddModal";
import EmailSentModal from "../EmailSentModal";
import EmailTemplateButton from "../EmailTemplateButton";

import EmailActionButton from "./EmailActionButton";
import styles from "./EmailComposeModal.module.scss";
import messages from "./messages";
import useAIInsertContent from "./useAIInsertContent";

interface CreateEmailModalProps extends DataAttributes {
  onHide: () => void;
}

const CreateEmailModal = ({
  currentUserId,
  emailActivityType,
  emailQuota,
  needOpenActivityModal,
  onDraftsFetch,
  onEmailAssociatedEntitiesFetch,
  onEmailQuotaFetch,
  onHide,
  onSend,
  signatureText,
  ...dataAttributes
}: CreateEmailModalProps & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();
  const analytics = useAnalytics();
  const history = useHistory();

  useEmailDraft();

  const draftSavedLabel = useDraftSavedLabel();

  const [modalStatus, setModalStatus] = useState<ModalStatus>("normal");

  const {
    aiWriterVisible,
    attachedFiles,
    body,
    bodyContainsDynamicVariables,
    composedBody,
    deals,
    emailTouched,
    entities,
    entitiesMissedFields,
    entityType,
    onAiWriterToggle,
    onEntitiesChange,
    onEntityTypeChange,
    onSendingChange,
    onSent,
    onUnsubscribeLinkIncludedChange,
    resources,
    sending,
    subject,
    subjectContainsDynamicVariables,
    template,
    unsubscribeLinkIncluded,
  } = useNonNullableContext(EmailContext);

  const [emailAssociatedEntities, setEmailAssociatedEntities] = useState<EmailAssociatedEntity[]>(
    []
  );

  const [createActivitiesModalOpen, showCreateActivitiesModal, hideCreateActivitiesModal] =
    useBoolean();
  const [addRecipientModalVisible, showAddRecipientModal, hideAddRecipientModal] = useBoolean();
  const [emailSentModalVisible, showEmailSendModal] = useBoolean();

  const bulkEmail = entities.length > 1;

  // Primary-recipient selection (multi-contact email): the user may star one
  // recipient as primary; otherwise it defaults to the first (server default).
  // Only meaningful when emailing contacts (primaryContactId is a contact).
  const [selectedPrimaryEntityId, setSelectedPrimaryEntityId] = useState<
    EntitiesSupportedByEmailFeature["id"] | undefined
  >();
  const isContactsEmail = entityType === EntityType.PERSON;
  const primaryRecipientId = resolvePrimaryEmailRecipientId(entities, selectedPrimaryEntityId);
  const handleSetPrimaryRecipient = useDynamicCallback(
    (entity: EntitiesSupportedByEmailFeature) => {
      setSelectedPrimaryEntityId(entity.id);
    }
  );

  const fixedCompany =
    entities.length === 1 &&
    entities[0].entity === EntityType.PERSON &&
    entities[0].accounts.length === 1
      ? entities[0].accounts[0]
      : undefined;

  const [formLayout, setFormLayout] = useState(() => activityLayoutModel.defaultFormLayout);
  const [schedule, setSchedule] = useState<EmailSchedule | undefined>();

  const recipientTypeSelectorDisabledTooltip = useMemo(() => {
    if (template) {
      return intl.formatMessage(
        messages.recipientTypeSelectorDisabledBecauseOfAppliedTemplateTooltip
      );
    }
    if (subjectContainsDynamicVariables || bodyContainsDynamicVariables) {
      return intl.formatMessage(
        messages.recipientTypeSelectorDisabledBecauseOfAppliedDynamicVariableTooltip
      );
    }
  }, [intl, template, subjectContainsDynamicVariables, bodyContainsDynamicVariables]);

  useEffect(() => {
    if (!emailQuota) {
      onEmailQuotaFetch();
    }
  }, [emailQuota, onEmailQuotaFetch]);

  // it is better to reload drafts each time
  // we are listening to objects that are using in drafts changes,
  // but some additional or not supporting updates are possible
  useEffect(() => {
    onDraftsFetch();
  }, [onDraftsFetch]);

  useEffect(() => {
    if (unsubscribeLinkIncluded === undefined && entities.length > 1) {
      onUnsubscribeLinkIncludedChange(true);
    }
  }, [unsubscribeLinkIncluded, onUnsubscribeLinkIncludedChange, entities]);

  const handleRemoveRecipient = useDynamicCallback(({ id }: EntitiesSupportedByEmailFeature) => {
    onEntitiesChange(entities.filter((recipient) => recipient.id !== id));
  });

  const handleAIEmailInsert = useAIInsertContent();

  const editableRenderer = useDynamicCallback((editable: React.ReactNode) =>
    aiWriterVisible ? (
      <AIInlineWriter
        emailBody={body}
        emailSubject={subject}
        entityId={entities.length === 1 ? entities[0].id : undefined}
        entityType={entities.length === 1 ? entities[0].entity : undefined}
        onClose={onAiWriterToggle}
        onInsert={handleAIEmailInsert}
        signatureText={signatureText}
      />
    ) : (
      editable
    )
  );

  const handleCreate = useDynamicCallback(
    ({
      activity,
      emailAssociatedEntities,
      fieldsValueValidateCallback,
      schedule,
    }: {
      activity?: ActivityCreationPayloadWithCustomFields;
      emailAssociatedEntities: EmailAssociatedEntity[];
      fieldsValueValidateCallback?: (customFieldErrors: CustomFieldError[]) => void;
      schedule?: EmailSchedule;
    }) => {
      if (entityType && formLayout) {
        const childLayout = formLayout.childLayouts?.find(
          ({ crmActivityType }) => crmActivityType?.name === DefaultActivityTypeName.EMAIL
        );
        const singleRecipientAssociations = !bulkEmail ? emailAssociatedEntities[0] : undefined;
        onSendingChange(true);
        onSend({
          callback: () => {
            onSendingChange(false);
            onSent();
            hideCreateActivitiesModal();
            if (schedule) {
              onHide();
            } else {
              showEmailSendModal();
            }

            analytics.completed(["Schedule Emails"], { scheduled: schedule });
          },
          email: {
            ...(attachedFiles.length
              ? {
                  attachments: attachedFiles
                    .filter((attachedFile) => attachedFile.file?.id)
                    .map((attachedFile) => attachedFile.file!.id),
                }
              : {}),
            associatedEntities: bulkEmail ? emailAssociatedEntities : [],
            body: composedBody,
            crmActivity: {
              ...(activity ?? {
                name: subject,
                ...(emailActivityType
                  ? { assigneeId: currentUserId, crmActivityType: { id: emailActivityType.id } }
                  : {}),
                ...(singleRecipientAssociations?.dealId
                  ? { deal: { id: singleRecipientAssociations.dealId } }
                  : {}),
                ...(singleRecipientAssociations?.accountId
                  ? { account: { id: singleRecipientAssociations.accountId } }
                  : {}),
                ...(singleRecipientAssociations?.contactId
                  ? { contact: { id: singleRecipientAssociations.contactId } }
                  : {}),
              }),
              // Multi-contact email: designate the primary contact (starred, or the
              // first recipient by default). Single/non-contact sends omit it.
              ...(bulkEmail && isContactsEmail && primaryRecipientId != null
                ? { primaryContactId: primaryRecipientId }
                : {}),
            },
            emailTemplateId: template?.id,
            entityIds: !bulkEmail ? [entities[0].id] : undefined,
            entityType: entityType,
            isHtml: true,
            layoutId: formLayout.id,
            ...(childLayout ? { childLayoutId: childLayout.id } : {}),
            logActivity: true,
            resourceIds: Array.from(resources.keys()),
            ...(schedule ? { schedule } : {}),
            subject,
            tracking: true,
          },
          failureCallback: (customFieldErrors) => {
            onSendingChange(false);
            fieldsValueValidateCallback?.(customFieldErrors);
            analytics.failed(["Schedule Emails"], { scheduled: schedule });
          },
        });
      }
    }
  );

  const handleActivityModalCreate = useDynamicCallback<EmailActivitiesCreateModalProps["onCreate"]>(
    ({ activity, fieldsValueValidateCallback, schedule }) => {
      handleCreate({ activity, emailAssociatedEntities, fieldsValueValidateCallback, schedule });
    }
  );

  const handleSendClick = useDynamicCallback(() => {
    onEmailAssociatedEntitiesFetch({
      callback: (emailAssociatedEntities) => {
        if (needOpenActivityModal) {
          setEmailAssociatedEntities(emailAssociatedEntities);
          showCreateActivitiesModal();
        } else {
          handleCreate({ emailAssociatedEntities, schedule: schedule });
        }
      },
      deals,
      recipients: entities,
      recipientType: entityType,
    });
  });

  const handleEntityOpen = useDynamicCallback<EmailEntityOpenCallback>((entityId, entityType) => {
    let entityTypePath: string;
    switch (entityType) {
      case EntityType.COMPANY:
        entityTypePath = Path.COMPANY;
        break;
      case EntityType.PERSON:
        entityTypePath = Path.PEOPLE;
        break;
    }

    history.push(`${entityTypePath}/${entityId}`);

    if (modalStatus === "normal") {
      onHide();
    }
  });

  const handleHide = useDynamicCallback(() => {
    analytics.clicked(["Closed"]);
    hideCreateActivitiesModal();
    onHide();
  });

  if (!entityType || !formLayout) {
    return null;
  }

  return (
    <AnalyticsContext.Provider value={analytics}>
      <Modal
        {...dataAttributes}
        className={styles.modal}
        closeIcon={
          emailTouched ? (
            <Popconfirm
              cancelText={intl.formatMessage(messages.closeConfirmationNo)}
              description={intl.formatMessage(messages.closeConfirmationTitle)}
              okText={intl.formatMessage(messages.closeConfirmationYes)}
              onConfirm={handleHide}
              overlayClassName={styles.confirmation}
            >
              <FontAwesomeIcon icon={faClose} />
            </Popconfirm>
          ) : undefined
        }
        dynamic
        footer={null}
        maskClosable={false}
        onCancel={emailTouched ? undefined : handleHide}
        onStatusChange={setModalStatus}
        open={!emailSentModalVisible}
        scrollable
        subtitle={
          emailQuota
            ? intl.formatMessage(messages.subTitle, {
                sent: emailQuota.remaining ?? 0,
                total: emailQuota.total ?? 0,
              })
            : undefined
        }
        title={
          <>
            <span>{intl.formatMessage(messages.title)}</span>
            {modalStatus !== "minimized" && (
              <EmailRecipientTypeSelect
                disabled={!!entities.length || !!recipientTypeSelectorDisabledTooltip}
                disabledTooltip={recipientTypeSelectorDisabledTooltip}
                onChange={onEntityTypeChange}
                value={entityType}
              />
            )}
          </>
        }
        width="42rem"
      >
        <EmailEditorsProvider>
          <div className={styles.container}>
            <div className={styles.buttonsContainer}>
              <EmailTemplateButton />
              <EmailDraftsButton onEntityOpen={handleEntityOpen} />
            </div>
            <EmailRecipientList
              action={
                <Button
                  onClick={showAddRecipientModal}
                  onMouseDown={(e) => e.preventDefault()}
                  size="small"
                  type="link"
                >
                  {intl.formatMessage(messages.addRecipientButtonLabel)}
                </Button>
              }
              className={styles.recipientList}
              entities={entities}
              entitiesMissedFields={entitiesMissedFields}
              label={intl.formatMessage(messages.to)}
              labelTooltip={intl.formatMessage(messages.multipleRecipientsInfo)}
              moreText={intl.formatMessage(messages.more)}
              onEntityRemove={handleRemoveRecipient}
              onSetPrimaryEntity={isContactsEmail ? handleSetPrimaryRecipient : undefined}
              primaryEntityId={primaryRecipientId}
              primaryTooltip={intl.formatMessage(messages.primaryRecipientTooltip)}
              setPrimaryTooltip={intl.formatMessage(messages.setPrimaryRecipientTooltip)}
            />
            <Divider className={styles.divider} />
            <AISubjectField className={styles.subject} />
            <Divider className={styles.divider} />
            <EmailBody
              action={
                <div className={styles.actionContainer}>
                  {draftSavedLabel && <span>{draftSavedLabel}</span>}
                  <EmailActionButton
                    emailSending={sending}
                    onClick={handleSendClick}
                    onScheduleChange={setSchedule}
                    schedule={schedule}
                  />
                </div>
              }
              aiWriterVisible={aiWriterVisible}
              className={styles.body}
              editableRenderer={editableRenderer}
            />
          </div>
        </EmailEditorsProvider>
      </Modal>
      {createActivitiesModalOpen && (
        <EmailActivitiesCreateModal
          emailAssociatedEntities={emailAssociatedEntities}
          fixedCompany={fixedCompany}
          formLayout={formLayout}
          onBack={hideCreateActivitiesModal}
          onCreate={handleActivityModalCreate}
          onFormLayoutChange={setFormLayout}
          onHide={onHide}
        />
      )}
      {emailSentModalVisible && <EmailSentModal onHide={onHide} />}
      <EmailPreviewModal />
      {addRecipientModalVisible && (
        <AddEmailRecipientsModal onHide={hideAddRecipientModal} onNavigated={onHide} />
      )}
    </AnalyticsContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => {
  const bulkEmailSetting = getUserSettingValue(state, UserSetting.BULK_EMAIL);

  return {
    currentUserId: getCurrentUser(state)!.id,
    emailActivityType: getEmailActivityType(state),
    emailQuota: getEmailQuota(state),
    needOpenActivityModal: doesNeedToOpenActivityFormAfterSendingEmail(state),
    signatureText: bulkEmailSetting.signature ? (bulkEmailSetting.signatureText ?? "") : "",
  };
};

const mapDispatchToProps = {
  onDraftsFetch: fetchEmailDrafts.request,
  onEmailAssociatedEntitiesFetch: fetchNewEmailAssociatedEntities,
  onEmailQuotaFetch: fetchEmailQuota.request,
  onSend: sendEmail,
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateEmailModal);
